// Lion Auto price ladder helpers: the order a pricer clicks our listings
// into (criteria.ladderIds) or inserts one at a typed rank, the ranks the
// server will number them with, the
// criteria a match reads (no ladder keys), where a stopped ladder can be
// continued and how the continuation merges in, what the floor did to a
// ladder, the bulk-price rows an apply sends and what the L poller tracks
// once they landed. Pure; tested in lionAutoLadder.test.js.

// The server's caps: 500 ranked listings (applied in bulk-price chunks of
// APPLY_CHUNK); offset and step are L dollars, 0..100, cents allowed
export const MAX_LADDER = 500;
export const MAX_LADDER_AMOUNT = 100;
export const DEFAULT_LADDER_OFFSET = 1;
export const DEFAULT_LADDER_STEP = 2;
// The floor: a % of each ranked listing's own cost no rank is priced under,
// 0..500, cents allowed; 0 = none of the group's own (the default): it uses
// the show's floor, else the artist's (utils/lionAutoFloors)
export const MAX_FLOOR_PCT = 500;
// bulk-price takes at most 50 updates per request
export const APPLY_CHUNK = 50;
// The activity-log label of a ladder apply (whitelisted by the server)
export const LADDER_SOURCE = 'lion_auto_ladder';

// The criteria keys that only price (saved with the group, never read by a
// match): stripped before every match call and match-cache key. The hold
// settings (utils/lionAutoHold) and the no-comp step-down rate
// (utils/lionAutoNoComp) are among them, so editing one never re-matches.
export const LADDER_KEYS = Object.freeze([
  'ladderIds', 'ladderOffset', 'ladderStep', 'ladderFloorPct', 'holdLive', 'holdSeats', 'holdPrice', 'noCompDropPct',
]);

const idOf = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/** Positive integer ids, deduped (the first position stays), at most MAX_LADDER */
export function cleanLadderIds(ids) {
  const out = [];
  const seen = new Set();
  for (const v of Array.isArray(ids) ? ids : []) {
    const id = idOf(v);
    if (id === null || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_LADDER) break;
  }
  return out;
}

/** An offset / step: 0..max (100) rounded to cents, else the fallback */
export function cleanLadderAmount(value, fallback, max = MAX_LADDER_AMOUNT) {
  let n = NaN;
  if (typeof value === 'number') n = value;
  else if (typeof value === 'string' && value.trim() !== '') n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > max) return fallback;
  return Math.round(n * 100) / 100;
}

/** The floor %: 0..500 rounded to cents; blank or junk = 0, no floor of the group's own */
export function cleanFloorPct(value) {
  return cleanLadderAmount(value, 0, MAX_FLOOR_PCT);
}

/** "130%" / "112.5%" */
export function pctText(pct) {
  return `${Number(pct)}%`;
}

/**
 * What a ladder answer is for, as one string: the group (null = a new one),
 * its cleaned criteria and the floor % the ladder prices at (the group's own,
 * else the inherited one: utils/lionAutoFloors effectiveFloor). An answer is
 * shown only while this stays the same, so a new floor in the Floors box
 * drops it as a criteria edit does. JSON.parse gives { groupId, criteria,
 * floorPct } back; a resume and any other key are left out, so a running
 * call's variables give the same string.
 */
export function ladderKeyFor({ groupId = null, criteria, floorPct }) {
  return JSON.stringify({ groupId: groupId ?? null, criteria, floorPct: cleanFloorPct(floorPct) });
}

/**
 * The criteria a match reads: everything but the ladder keys, which only
 * price. Ranking a listing or setting the floor must never cost a match call.
 */
export function withoutLadder(criteria) {
  if (!criteria || typeof criteria !== 'object') return criteria;
  const out = { ...criteria };
  for (const key of LADDER_KEYS) delete out[key];
  return out;
}

/**
 * A click on one of our listings: an unranked id joins at the end (the next
 * rank), a ranked one leaves and the ranks after it close up. The same array
 * comes back when nothing changes (the cap is reached, or a junk id).
 */
export function toggleLadderId(ladderIds, inventoryId, max = MAX_LADDER) {
  const list = Array.isArray(ladderIds) ? ladderIds : [];
  const id = idOf(inventoryId);
  if (id === null) return list;
  const at = list.findIndex(x => Number(x) === id);
  if (at >= 0) return [...list.slice(0, at), ...list.slice(at + 1)];
  return list.length >= max ? list : [...list, id];
}

/**
 * A rank typed for one of our listings: it goes in just before beforeId (the
 * listing shown at that rank: idAtRank), so that rank and every one above it
 * move up one and nothing else changes; beforeId null = at the end (the next
 * rank, as a click). A ranked id moves (it leaves its old place first). The
 * same array comes back when nothing changes: a junk id, a new id at the cap,
 * a beforeId not in the list (the ranking changed since it was read; the
 * server refuses that too, RANKING_CHANGED), or the order already so. Never
 * changes its input.
 * @param {Array<number|string>} ladderIds  the saved order (criteria.ladderIds)
 * @param {number|string} inventoryId
 * @param {number|string|null} [beforeId]
 * @param {number} [max]
 * @returns {Array<number|string>}
 */
export function insertLadderId(ladderIds, inventoryId, beforeId = null, max = MAX_LADDER) {
  const list = Array.isArray(ladderIds) ? ladderIds : [];
  const id = idOf(inventoryId);
  if (id === null) return list;
  const rest = list.filter(x => Number(x) !== id);
  if (rest.length === list.length && list.length >= max) return list;
  let at = rest.length;
  if (beforeId != null) {
    const before = idOf(beforeId);
    at = before === null ? -1 : rest.findIndex(x => Number(x) === before);
    if (at < 0) return list;
  }
  const next = [...rest.slice(0, at), id, ...rest.slice(at)];
  const same = next.length === list.length && next.every((x, i) => Number(x) === Number(list[i]));
  return same ? list : next;
}

/**
 * The listing shown at `rank` in a ladderRanks map (id string → rank), as a
 * number id; null when no listing holds it (past the last rank: insert at
 * the end) or `rank` is no whole number from 1.
 * @param {Map<string, number>} rankById
 * @param {number|string} rank
 * @returns {number|null}
 */
export function idAtRank(rankById, rank) {
  const n = idOf(rank);
  if (n === null || !(rankById instanceof Map)) return null;
  for (const [key, r] of rankById) if (r === n) return idOf(key);
  return null;
}

/**
 * The ranks a listing not ranked yet can be inserted at: ranks as the table
 * numbers them (ladderRanks: members only), max = the next rank (ranks.size
 * + 1, at the end).
 * @param {Array<number|string>} ladderIds
 * @param {Set<string>|null} [memberIds]  as ladderRanks takes them
 * @returns {{ ranks: Map<string, number>, max: number }}
 */
export function rankChoice(ladderIds, memberIds = null) {
  const ranks = ladderRanks(ladderIds, memberIds);
  return { ranks, max: ranks.size + 1 };
}

/**
 * A typed rank: a whole number 1..max, from a box's text or a number; null
 * for anything else (blank, junk, a fraction, 0, past max, no usable max).
 * @param {string|number} value
 * @param {number} max
 * @returns {number|null}
 */
export function cleanRank(value, max) {
  let n = NaN;
  if (typeof value === 'number') n = value;
  else if (typeof value === 'string' && value.trim() !== '') n = Number(value);
  const top = Number(max);
  if (!Number.isInteger(n) || n < 1 || !Number.isFinite(top) || n > top) return null;
  return n;
}

/**
 * The rank one listing holds, numbered as the table shows them (ladderRanks:
 * members only): where an insert at a typed rank landed, which differs from
 * the rank typed when the ranking changed ahead of its beforeId. null when
 * it is not ranked, not a member now, or the id is junk.
 * @param {Array<number|string>} ladderIds  the saved order (criteria.ladderIds)
 * @param {Set<string>|null} memberIds  as ladderRanks takes them
 * @param {number|string} inventoryId
 * @returns {number|null}
 */
export function rankOf(ladderIds, memberIds, inventoryId) {
  const id = idOf(inventoryId);
  if (id === null) return null;
  return ladderRanks(ladderIds, memberIds).get(String(id)) ?? null;
}

/**
 * Inventory id (string) → rank (1 = the lowest price) as the server numbers
 * the ladder: the saved order, skipping ids that are not members now (sold,
 * or out of the group's sections / filters), so the ranks after them close
 * up. memberIds: a Set of id strings, or null while unknown — then every
 * saved id keeps its position.
 */
export function ladderRanks(ladderIds, memberIds = null) {
  const ranks = new Map();
  for (const v of Array.isArray(ladderIds) ? ladderIds : []) {
    const key = String(v);
    if (ranks.has(key) || (memberIds && !memberIds.has(key))) continue;
    ranks.set(key, ranks.size + 1);
  }
  return ranks;
}

const isPrice = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0;

/** The rungs an apply can write: status OK with a usable new B */
export function applicableRungs(rungs) {
  return (Array.isArray(rungs) ? rungs : []).filter(r => r && r.status === 'OK' && isPrice(r.newB));
}

// The status codes of an answer that leaves ranks without a price (a run
// that stopped, no anchor comp, nothing ranked or no sections) or prices
// them by the no-comp step-down (the Auto runner's to write): never written
const INCOMPLETE_CODES = ['PARTIAL', 'WHATIF_FAILED', 'NO_ANCHOR_COMP', 'NO_RANKED_MEMBERS', 'NO_SECTIONS', 'STEP_DOWN'];
// An answer with only these codes and STEP_DOWN among them is the no-comp
// step-down's (utils/lionAutoNoComp): no anchor comp, the ranks priced the
// way the Auto runner would price them
const STEP_DOWN_CODES = ['NO_ANCHOR_COMP', 'STEP_DOWN'];
// A rung with its price: OK with a usable new B, or already at it
const pricedRung = (r) => !!r && ((r.status === 'OK' && isPrice(r.newB)) || r.status === 'UNCHANGED');

/**
 * What the end of a Calculate (its Continues merged in) does with the answer
 * on screen. Complete = at least one rung, every rung priced (OK with a
 * usable new B, or UNCHANGED), none of INCOMPLETE_CODES, nothing to continue
 * from (resume null) and priced at the floor the group uses now (not
 * floorStale): 'unchanged' when every rung is UNCHANGED, else 'write'.
 * Anything else is 'incomplete' and writes nothing: a rank left without a
 * price keeps its old B and can invert the order (holding on with no anchor
 * comp would write only the held ranks). 'stepDown' (after the same empty /
 * resume / floor checks): the no-comp step-down priced the ranks, the codes
 * STEP_DOWN and at most NO_ANCHOR_COMP besides; it writes nothing either
 * (the Auto runner steps the prices down on its own clock), and a Save may
 * still turn Auto on.
 * @param {object|null} data  the ladder answer
 * @param {{ resume?: object|null, floorStale?: boolean }} [opts]  ladderResume(data); ladderFloorChanged(...)
 * @returns {'write'|'unchanged'|'incomplete'|'stepDown'}
 */
export function ladderEndAction(data, { resume = null, floorStale = false } = {}) {
  const rungs = Array.isArray(data?.rungs) ? data.rungs : [];
  if (!rungs.length || resume || floorStale) return 'incomplete';
  const codes = Array.isArray(data.status) ? data.status : [];
  if (codes.includes('STEP_DOWN') && codes.every(c => STEP_DOWN_CODES.includes(c))) return 'stepDown';
  if (codes.some(c => INCOMPLETE_CODES.includes(c)) || !rungs.every(pricedRung)) return 'incomplete';
  return rungs.every(r => r.status === 'UNCHANGED') ? 'unchanged' : 'write';
}

// The status code whose banner already explains a rung left without a price
// for this reason (rung.reason)
const REASON_CODES = new Map([['DEADLINE', 'PARTIAL'], ['WHATIF_FAILED', 'WHATIF_FAILED'], ['NO_ANCHOR_COMP', 'NO_ANCHOR_COMP']]);

/**
 * The rungs left without a price that no status code of the answer explains
 * (a flat what-if, no bracket, an unreachable target, or an OK rung with no
 * usable B), in rank order: the ladder bar names them, since each one stops
 * the write. The rung objects themselves, never copied nor changed.
 * @returns {object[]}
 */
export function unpricedRungs(data) {
  const codes = new Set(Array.isArray(data?.status) ? data.status : []);
  return (Array.isArray(data?.rungs) ? data.rungs : [])
    .filter(r => r && !pricedRung(r) && !codes.has(REASON_CODES.get(r.reason)))
    .sort(byRank);
}

/**
 * bulk-price rows for rungs: the new B; the B the ladder was computed from
 * as the stale-price guard (the server refuses a row whose B moved since);
 * the Vivid id and zone flag for the server's post-write what-if, as
 * Compare's save sends them
 */
export function ladderUpdates(rungs) {
  return (Array.isArray(rungs) ? rungs : []).map(r => ({
    inventoryId: r.inventoryId,
    newPrice: r.newB,
    expectedCurrentPrice: Number(r.currentB) || 0,
    vividListingId: r.vividListingId ?? null,
    zone: !!r.zone,
  }));
}

/**
 * What the L poller (useLPricePoll) needs once prices landed — Compare's
 * handleBulkApplied rule: Skybox's what-if L to pin per written row (when the
 * response carried one), rows that were not on Vivid before the write
 * (tracked until their first L) and rows that were (verified until Vivid's
 * real L moves; one whose what-if equals its old L has nothing to verify,
 * the poller skips it too).
 * @param {object[]} written  bulk-price results ({ inventoryId, newPrice, unitPrice })
 * @param {Map<string, object>} before  inventory id → our row as cached before the write
 * @returns {{ pins: object[], write: object[], verify: object[] }}
 */
export function lTrackingFor(written, before) {
  const pins = [];
  const write = [];
  const verify = [];
  for (const r of Array.isArray(written) ? written : []) {
    const prev = before?.get(String(r.inventoryId));
    if (r.unitPrice > 0) pins.push({ id: r.inventoryId, unitPrice: r.unitPrice, listPrice: r.newPrice });
    if (!(r.newPrice > 0)) continue; // $0 = delisted, no L to wait for
    const entry = { id: r.inventoryId, expectedListPrice: r.newPrice, vividListingId: prev?.vividListingId };
    if (!(prev?.unitPrice > 0 && prev?.vividListingId)) {
      write.push(entry);
    } else if (!(r.unitPrice > 0 && Math.abs(r.unitPrice - prev.unitPrice) < 0.005)) {
      verify.push({ ...entry, previousUnitPrice: prev.unitPrice, whatIf: r.unitPrice ?? null });
    }
  }
  return { pins, write, verify };
}

const byRank = (a, b) => (Number(a.rank) || 0) - (Number(b.rank) || 0);
const resolvedRung = (r) => r?.status === 'OK' || r?.status === 'UNCHANGED';
// A held rank (holding on: utils/lionAutoHold) is priced at the hold price
// with no what-if on every answer, a continuation's too, so it never counts
// toward where a stopped ladder picks up
const liveRung = (r) => !!r && r.tier !== 'HELD';

// A rung the run actually tried: priced, or given up on for a reason of its
// own. Not tried: DEADLINE rungs (cut short or never reached), rungs the
// run skipped after giving up on Skybox's what-if (WHATIF_FAILED and never
// given a target L) and held rungs.
function triedRung(r) {
  if (!liveRung(r)) return false;
  if (resolvedRung(r)) return true;
  return r?.status === 'UNRESOLVED' && r.reason !== 'DEADLINE' && r.targetL !== null && r.targetL !== undefined;
}

/**
 * Where a ladder that stopped early (PARTIAL) can pick up: after the highest
 * rank the run tried, stepping up from the L of the last rung that resolved
 * (an UNCHANGED rung's newL is its actual L; a rung held at its floor is an
 * OK / UNCHANGED one whose newL is the floor price's L, so the rest climbs
 * from the floor, as one uninterrupted run would). Only LIVE rungs count
 * (holding off, every rung is): afterRank, the rest, the last resolved rung
 * and the total are all read from them. Null when nothing is left
 * to price, or nothing resolved to step up from — then only a fresh
 * Calculate makes sense.
 * @returns {{ afterRank: number, prevL: number, from: number, to: number, total: number }|null}
 */
export function ladderResume(result) {
  const rungs = (Array.isArray(result?.rungs) ? result.rungs.filter(liveRung) : []).sort(byRank);
  let afterRank = null;
  for (const r of rungs) if (triedRung(r)) afterRank = Number(r.rank);
  if (afterRank === null) return null;
  const rest = rungs.filter(r => Number(r.rank) > afterRank);
  const last = rungs.filter(r => resolvedRung(r) && Number(r.rank) <= afterRank && Number(r.newL) > 0).pop();
  if (!rest.length || !last) return null;
  return {
    afterRank,
    prevL: Number(last.newL),
    from: Number(rest[0].rank),
    to: Number(rest[rest.length - 1].rank),
    total: rungs.length,
  };
}

// Two answers' floors ({ pct } objects; null pct = none) differ in cents. An
// answer with no floor at all (an older server) has nothing to compare.
function floorsDiffer(a, b) {
  if (!a || typeof a !== 'object' || !b || typeof b !== 'object') return false;
  return Math.round(cleanFloorPct(a.pct) * 100) !== Math.round(cleanFloorPct(b.pct) * 100);
}

// The server's status codes in its order (unknown ones follow as they came)
const STATUS_ORDER = ['NO_SECTIONS', 'NO_ANCHOR_COMP', 'NO_RANKED_MEMBERS', 'PARTIAL', 'WHATIF_FAILED', 'STEP_DOWN'];

/**
 * A continuation (the answer to a resume after `afterRank`) merged into the
 * ladder it continues: rungs up to afterRank stay, the ones after are the
 * continuation's (each with its own floor flags). PARTIAL stays only if the
 * continuation stopped too, WHATIF_FAILED follows the merged rungs, what-if
 * calls add up; the anchor comp, the comps' age and the floor summary stay
 * the first run's (floor.appliedTo counts the returned rungs that have a
 * floor: the first run returned every rank, a continuation only the ranks
 * after afterRank, the same listings at the same cost and %; what the floor
 * did across the merged ladder is read from its rungs: floorSummary).
 * With holding on the continuation re-sends every HELD rung (they rank
 * after the live ones), so they line up and are replaced like the rest, and
 * its `hold` (the whole group's live ranks / seats) is the merged one. Null
 * when the continuation doesn't line up with the stored ranks (same ranks, same listings) — a
 * listing sold or bought in between — or was priced at another floor (both
 * say their floor and the two differ in cents: the show's or the artist's
 * floor changed in between): then only a fresh Calculate is right.
 */
export function mergeLadder(prev, next, afterRank) {
  if (floorsDiffer(prev?.floor, next?.floor)) return null;
  const prevRungs = Array.isArray(prev?.rungs) ? prev.rungs : [];
  const nextRungs = Array.isArray(next?.rungs) ? next.rungs : [];
  const tail = new Map(prevRungs.filter(r => Number(r.rank) > afterRank).map(r => [Number(r.rank), String(r.inventoryId)]));
  const ranks = new Set(nextRungs.map(r => Number(r.rank)));
  const linesUp = nextRungs.length === tail.size && ranks.size === tail.size
    && nextRungs.every(r => tail.get(Number(r.rank)) === String(r.inventoryId));
  if (!linesUp) return null;

  const rungs = [...prevRungs.filter(r => Number(r.rank) <= afterRank), ...nextRungs].sort(byRank);
  const codes = new Set((Array.isArray(next.status) ? next.status : []).filter(c => c !== 'WHATIF_FAILED'));
  if (rungs.some(r => r.reason === 'WHATIF_FAILED')) codes.add('WHATIF_FAILED');
  return {
    ...prev,
    rungs,
    unranked: next.unranked ?? prev.unranked,
    dropped: next.dropped ?? prev.dropped,
    status: [...STATUS_ORDER.filter(c => codes.has(c)), ...[...codes].filter(c => !STATUS_ORDER.includes(c))],
    whatIfCalls: (Number(prev.whatIfCalls) || 0) + (Number(next.whatIfCalls) || 0),
    inOtherGroups: { ...(prev.inOtherGroups || {}), ...(next.inOtherGroups || {}) },
    belowCost: { ...(prev.belowCost || {}), ...(next.belowCost || {}) },
    floor: prev.floor ?? next.floor ?? null,
    // The continuation's holding: its live ranks / seats are the whole
    // group's, counted before the resume slice (an older server's
    // continuation sends none: the first answer's)
    hold: next.hold !== undefined ? next.hold : prev.hold,
  };
}

const positivePct = (v) => {
  const n = Number(v);
  return v !== null && v !== undefined && v !== '' && Number.isFinite(n) && n > 0 ? n : null;
};

// Where the server's floor came from (the ladder answer's floor.source)
const FLOOR_SOURCES = ['group', 'show', 'artist'];

/**
 * What the floor did to a ladder answer, from its rungs (so a merged
 * continuation counts right): the floor % (null = no floor), the ladder rungs
 * held at their floor (the ranks above step up from them), the HELD rungs
 * (holding on: utils/lionAutoHold) whose floor beat the hold price, counted
 * apart since nothing steps up from them, the rungs the floor skipped for
 * want of a cost, and where the floor came from when the answer says (the
 * group's own, the show's or the artist's; null otherwise).
 * @returns {{ pct: number|null, held: number, overHold: number, noCost: number,
 *   source: 'group'|'show'|'artist'|null }}
 */
export function floorSummary(result) {
  const rungs = Array.isArray(result?.rungs) ? result.rungs : [];
  const source = result?.floor?.source;
  const floored = rungs.filter(r => r?.floored === true);
  return {
    pct: positivePct(result?.floor?.pct),
    held: floored.filter(liveRung).length,
    overHold: floored.filter(r => !liveRung(r)).length,
    noCost: rungs.filter(r => r?.floorNote === 'NO_COST').length,
    source: FLOOR_SOURCES.includes(source) ? source : null,
  };
}

/**
 * A held rung's tooltip: the floor B with how it was set when the server
 * sent it (only to users who can see cost), else the plain fact
 */
export function floorHeldText(rung, pct) {
  const b = rung?.floorB;
  if (b === null || b === undefined || b === '' || !Number.isFinite(Number(b))) return 'Held at its floor';
  const p = positivePct(pct);
  return `Held at the floor $${Number(b).toFixed(2)}${p !== null ? ` (cost × ${pctText(p)})` : ''}`;
}

/** A NO_COST rung's tooltip */
export const NO_COST_FLOOR_TEXT = 'The floor is on, but this listing has no cost in Skybox, so no floor applies to it';

/** Consecutive slices of at most `size` (bulk-price batches) */
export function chunked(list, size = APPLY_CHUNK) {
  const items = Array.isArray(list) ? list : [];
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

// Why the server left a rung UNRESOLVED (rung.reason), in plain words
const UNRESOLVED_REASONS = {
  NO_ANCHOR_COMP: 'No anchor comp to price against',
  TARGET_UNREACHABLE: "No B gives an L at or under this rank's target",
  NO_BRACKET: "Even the highest B stays under this rank's target L",
  PROBE_LIMIT: "Skybox's what-if did not pin the B down in 6 calls",
  WHATIF_FAILED: "Skybox's what-if gave no answer",
  DEADLINE: 'The run ran out of time before this rank',
};

/** Why a rung has no price, for a tooltip */
export function unresolvedText(reason) {
  return UNRESOLVED_REASONS[reason] || "Skybox's what-if gave no B that reaches this rank's L";
}

/** "+$4.00" / "−$3.50" / "±$0.00"; "—" when there is no number */
export function signedMoney(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '−' : '±';
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}
